import { z } from 'zod';
import { ReactElement, useMemo, useCallback, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { ApiBaseQueryError, ModelName } from '@casops/store/api';
import { ErrorResponse, SuccessNotificationContext } from '@dmc/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  EditAssetsAssignConfigurationGroupFormModel,
  EditAssetsAssignConfigurationGroupModel,
  useEditAssetsAssignConfigurationGroupMutation,
} from '@casops/store/apis/configurationGroupsApi';
import { UseAssetsAssignConfigurationGroupLoadModelDataReturn } from '@casops/components/layout/model-form/useAssetsAssignConfigurationGroupLoadModelData';
import ModelForm from '@casops/components/layout/model-form/ModelForm';
import CasFormDropdown from '@cas/components/inputs/cas-form-dropdown/CasFormDropdown';
import { closeSidePanel } from '@casops/store/slices/globalSlice';
import styles from './AssetsAssignConfigurationGroupForm.module.scss';
import { useAppDispatch } from '@casops/store/hooks';

interface FormModel
  extends EditAssetsAssignConfigurationGroupModel,
    EditAssetsAssignConfigurationGroupFormModel {}

interface AssetsAssignConfigurationGroupFormProps {
  configurationGroups: UseAssetsAssignConfigurationGroupLoadModelDataReturn['configurationGroups'];
  defaultConfigurationId: UseAssetsAssignConfigurationGroupLoadModelDataReturn['defaultConfigurationId'];
  selectedAssetIds: UseAssetsAssignConfigurationGroupLoadModelDataReturn['selectedAssetIds'];
}

type NameIdBasicModel = {
  id: number;
  n: string;
};

export type BulkAssignIncompatibleAssetsResult = {
  incompatibleAssets: NameIdBasicModel[];
};

export const isIncompatibleAssetsError = (
  data: unknown,
): data is BulkAssignIncompatibleAssetsResult => {
  return (
    data !== null &&
    typeof data === 'object' &&
    'incompatibleAssets' in data &&
    Array.isArray(data.incompatibleAssets) &&
    data.incompatibleAssets.every(
      (item: unknown) =>
        item !== null &&
        typeof item === 'object' &&
        'id' in item &&
        typeof item.id === 'number' &&
        'n' in item &&
        typeof item.n === 'string',
    )
  );
};

const modelName = ModelName.Asset;

export const AssetsAssignConfigurationGroupForm = ({
  configurationGroups,
  defaultConfigurationId,
  selectedAssetIds,
}: AssetsAssignConfigurationGroupFormProps): ReactElement => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
  const { showSuccessMessage } = useContext(SuccessNotificationContext);

  const configGroupOptions = useMemo(
    () =>
      configurationGroups?.map(({ id, name }) => ({
        key: id.toString(),
        value: id,
        stringValue: id.toString(),
        label: name,
      })) ?? [],
    [configurationGroups],
  );

  const schema = useMemo(
    () =>
      z.object({
        name: z
          .number()
          .nullable()
          .refine(v => v !== null, { message: t('Validation.SpecifyConfigurationGroup') }),
        assetIds: z.array(z.number()).min(1, { message: t('Validation.SpecifyAssets') }),
      }),
    [t],
  );

  const defaultValues: FormModel = {
    name: typeof defaultConfigurationId === 'number' ? defaultConfigurationId : null,
    assetIds: selectedAssetIds ?? [],
  };

  const { control, handleSubmit, formState, setValue, watch } = useForm<FormModel>({
    defaultValues,
    resolver: zodResolver(schema),
    mode: 'all',
  });

  // Keep assetIds in sync if selection changes externally
  useMemo(() => {
    setValue('assetIds', selectedAssetIds ?? [], { shouldValidate: true });
  }, [selectedAssetIds, setValue]);

  const currentName = watch('name');

  const [triggerAssign, { isLoading }] = useEditAssetsAssignConfigurationGroupMutation();

  const successCallback = useCallback((): void => {
    dispatch(closeSidePanel());
  }, [dispatch]);

  const onSubmitInternal = useCallback(
    async (values: FormModel) => {
      try {
        if (values.name === null) return;
        await triggerAssign({ id: values.name, assetIds: values.assetIds }).unwrap();

        // Show success message and close the side panel.
        showSuccessMessage(
          t('Model.Assign.Success', { modelName, count: selectedAssetIds?.length ?? 0 }),
        );
        successCallback();
      } catch (e: ApiBaseQueryError) {
        const errorResponse = new ErrorResponse<BulkAssignIncompatibleAssetsResult>(
          e,
          isIncompatibleAssetsError,
        );
        let message: string = errorResponse.getTitle();
        const customErrorData = errorResponse.getCustomError();

        if (customErrorData) {
          const assetNames = customErrorData.incompatibleAssets.map(a => a.n).join(', ');
          message = `${t('IncompatibleAssets')}: ${assetNames}`;
        }

        setErrorMessage(`${t('Failed!')} ${message}`);
      }
    },
    [selectedAssetIds?.length, showSuccessMessage, successCallback, t, triggerAssign],
  );

  const onSubmit = useCallback(() => {
    handleSubmit(onSubmitInternal)();
  }, [handleSubmit, onSubmitInternal]);

  const canSubmit =
    !isLoading &&
    formState.isValid &&
    formState.dirtyFields &&
    defaultValues.assetIds.length > 0 &&
    currentName !== defaultValues.name;

  return (
    <ModelForm
      canSubmit={canSubmit}
      onSubmit={onSubmit}
      errorMessage={errorMessage}
      control={control}
      submitLabel={t('Assign')}
    >
      <div className={styles.subtitle}>{t('AssetsAssignSubtitle')}</div>
      <CasFormDropdown<number, FormModel>
        key="name"
        control={control}
        name="name"
        label={t('Fields.ConfigurationGroup')}
        placeholder={t('Select')}
        items={configGroupOptions}
      />
    </ModelForm>
  );
};

export default AssetsAssignConfigurationGroupForm;
